import { Ban, Check, Clock, Minus, Pencil } from 'lucide-react';

import { cn } from '@/_shared/lib/utils';
import type { ApprovalPayload, InteractiveAnswer, QuestionBlockPayload } from '@/_shared/types';

import { summarizeEntry } from './answer-helpers';

type QuestionAnswer = Extract<InteractiveAnswer, { kind: 'question' }>;
type ApprovalDecision = Extract<InteractiveAnswer, { kind: 'approval' }>;

const ROW =
  'mx-6 my-2 flex animate-slide-up items-start gap-2.5 rounded-xl border border-border bg-card px-3.5 py-2.5 text-sm';

/** Shared treatment for the non-answer terminal states (skipped / dismissed / timed out): a
 * plain-border icon tile + the question (no strikethrough) + a short note. */
function NonAnswerSummary({
  icon,
  question,
  note,
}: {
  icon: React.ReactNode;
  question?: string;
  note: string;
}): React.ReactElement {
  return (
    <div className={ROW}>
      <span
        aria-hidden
        className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground"
      >
        {icon}
      </span>
      <div className="min-w-0">
        {question && <div className="text-xs text-muted-foreground">{question}</div>}
        {/* Same hierarchy as the answered summary (small question, prominent answer line) but greyed —
            it's a non-answer. */}
        <div className="font-medium text-muted-foreground">{note}</div>
      </div>
    </div>
  );
}

/** Compact terminal summary for a resolved question block, rendered in the transcript. */
export function QuestionAnswerSummary({
  answer,
  payload,
}: {
  answer: QuestionAnswer;
  payload?: QuestionBlockPayload;
}): React.ReactElement {
  const promptFor = (questionId: string): string | undefined =>
    payload?.questions.find((q) => q.id === questionId)?.prompt;
  const question = payload ? headlineFor(payload) : undefined;

  if (answer.status === 'dismissed') {
    return <NonAnswerSummary icon={<Ban className="size-3" />} question={question} note="Dismissed — do not proceed" />;
  }
  if (answer.status === 'skipped') {
    return <NonAnswerSummary icon={<Minus className="size-3" />} question={question} note="Skipped — no preference" />;
  }
  if (answer.status === 'timedout') {
    return <NonAnswerSummary icon={<Clock className="size-3" />} question={question} note="No response" />;
  }

  return (
    <div className={ROW}>
      <span
        aria-hidden
        className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md bg-success-bg text-success"
      >
        <Check className="size-3" strokeWidth={3} />
      </span>
      <div className="min-w-0 space-y-1.5">
        {answer.entries.map((entry) => (
          <div key={entry.questionId}>
            {promptFor(entry.questionId) && (
              <div className="text-xs text-muted-foreground">{promptFor(entry.questionId)}</div>
            )}
            {/* A skipped question inside an answered block is greyer so it doesn't read as a real answer. */}
            <div className={cn('font-medium', entry.kind === 'skipped' ? 'text-muted-foreground' : 'text-foreground')}>
              {summarizeEntry(entry)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function headlineFor(payload: QuestionBlockPayload): string {
  if (payload.questions.length > 1) return `${payload.questions.length} questions`;
  return payload.questions[0]?.prompt ?? 'Question';
}

// Same neutral card as the question summaries; only the icon tile varies by decision.
const APPROVAL_CONFIG: Record<
  ApprovalDecision['decision'],
  { tile: string; Icon: typeof Check; label: string; strokeWidth?: number }
> = {
  approved: { tile: 'bg-success-bg text-success', Icon: Check, label: 'Approved', strokeWidth: 3 },
  rejected: { tile: 'bg-danger-bg text-danger', Icon: Ban, label: 'Rejected' },
  revised: { tile: 'bg-highlight-bg text-highlight', Icon: Pencil, label: 'Revised' },
  // Dismissed is neutral (not a rejection) — same plain treatment as a dismissed question.
  dismissed: { tile: 'border border-border text-muted-foreground', Icon: Ban, label: 'Dismissed' },
  timedout: { tile: 'border border-border text-muted-foreground', Icon: Clock, label: 'No response' },
};

/** Terminal summary for a resolved approval block — a colored icon tile + the title (what it was for)
 * + the decision, matching the question answer summary. */
export function ApprovalResultBanner({
  answer,
  payload,
}: {
  answer: ApprovalDecision;
  payload?: ApprovalPayload;
}): React.ReactElement {
  const { tile, Icon, label, strokeWidth } = APPROVAL_CONFIG[answer.decision];

  return (
    <div className={ROW}>
      <span aria-hidden className={cn('mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md', tile)}>
        <Icon className="size-3" strokeWidth={strokeWidth ?? 2.5} />
      </span>
      <div className="min-w-0">
        {/* The approval title = what it was for (like the question in a question summary). */}
        {payload?.title && <div className="text-xs text-muted-foreground">{payload.title}</div>}
        <div className="font-medium text-foreground">{label}</div>
        {/* A revision's substance is the comment — surface it so the transcript records what was asked. */}
        {answer.decision === 'revised' && answer.comment && (
          <div className="mt-0.5 text-sm text-muted-foreground italic">“{answer.comment}”</div>
        )}
      </div>
    </div>
  );
}
